
import React, { useState, useEffect } from 'react';
import {
    Search,
    Plus,
    MoreVertical,
    Edit2,
    Trash2,
    Shield,
    ShieldAlert,
    Mail,
    Calendar
} from 'lucide-react';
import { adminApi } from '../services/api';
import AdminUserModal from '../components/AdminUserModal';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';

const AdminUsers = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    // Modal state
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        try {
            setLoading(true);
            const response = await adminApi.getAdminUsers();
            setUsers(response.data.users);
        } catch (error) {
            console.error('Failed to fetch admin users:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = () => {
        setSelectedUser(null);
        setIsModalOpen(true);
    };

    const handleEdit = (user) => {
        setSelectedUser(user);
        setIsModalOpen(true);
    };

    const handleDelete = async (id) => {
        if (window.confirm('Bu yöneticiyi silmek istediğinize emin misiniz?')) {
            try {
                await adminApi.deleteAdminUser(id);
                setUsers(users.filter(u => u.id !== id));
            } catch (error) {
                console.error('Failed to delete user:', error);
                alert('Yönetici silinirken bir hata oluştu.');
            }
        }
    };

    const handleSave = async (formData) => {
        try {
            setIsSaving(true);
            if (selectedUser) {
                // Update
                const response = await adminApi.updateAdminUser(selectedUser.id, formData);
                setUsers(users.map(u => u.id === selectedUser.id ? response.data.user : u));
            } else {
                // Create
                const response = await adminApi.createAdminUser(formData);
                setUsers([response.data.user, ...users]);
            }
            setIsModalOpen(false);
        } catch (error) {
            console.error('Failed to save user:', error);
            alert(error.response?.data?.message || 'Bir hata oluştu.');
        } finally {
            setIsSaving(false);
        }
    };

    const filteredUsers = users.filter(user =>
        user.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Yöneticiler</h1>
                    <p className="text-gray-500 mt-1">Sistem yöneticilerini ve yetkilerini yönetin</p>
                </div>
                <button
                    onClick={handleCreate}
                    className="btn-primary flex items-center justify-center gap-2 px-4 py-2.5"
                >
                    <Plus size={20} />
                    <span>Yeni Yönetici</span>
                </button>
            </div>

            {/* Stats Cards using Glass Effect */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="glass p-6 rounded-2xl relative overflow-hidden group">
                    <div className="absolute right-0 top-0 w-32 h-32 bg-primary-100/50 rounded-full blur-3xl -mr-16 -mt-16 transition-all group-hover:scale-110"></div>
                    <div>
                        <p className="text-sm font-medium text-gray-500 mb-1">Toplam Yönetici</p>
                        <h3 className="text-3xl font-bold text-gray-900">{users.length}</h3>
                    </div>
                </div>
                <div className="glass p-6 rounded-2xl relative overflow-hidden group">
                    <div className="absolute right-0 top-0 w-32 h-32 bg-purple-100/50 rounded-full blur-3xl -mr-16 -mt-16 transition-all group-hover:scale-110"></div>
                    <div>
                        <p className="text-sm font-medium text-gray-500 mb-1">Süper Adminler</p>
                        <h3 className="text-3xl font-bold text-gray-900">
                            {users.filter(u => u.role === 'super_admin').length}
                        </h3>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="glass rounded-2xl overflow-hidden border border-white/20 shadow-xl">
                {/* Toolbar */}
                <div className="p-4 border-b border-gray-100 flex items-center gap-4 bg-white/50">
                    <div className="relative flex-1 max-w-md">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                        <input
                            type="text"
                            placeholder="İsim veya e-posta ile ara..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 bg-white border border-gray-200 rounded-xl text-sm focus:border-primary-300 focus:ring-4 focus:ring-primary-100 transition-all outline-none"
                        />
                    </div>
                </div>

                {/* Table */}
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-gray-50/80 text-xs uppercase text-gray-500 font-semibold">
                            <tr>
                                <th className="px-6 py-4 text-left">Yönetici</th>
                                <th className="px-6 py-4 text-left">Rol</th>
                                <th className="px-6 py-4 text-left">Durum</th>
                                <th className="px-6 py-4 text-left">Kayıt Tarihi</th>
                                <th className="px-6 py-4 text-right">İşlemler</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 bg-white/50">
                            {loading ? (
                                <tr>
                                    <td colSpan="5" className="px-6 py-12 text-center text-gray-500">
                                        <div className="flex flex-col items-center justify-center">
                                            <div className="w-8 h-8 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin mb-4"></div>
                                            <p>Yükleniyor...</p>
                                        </div>
                                    </td>
                                </tr>
                            ) : filteredUsers.length === 0 ? (
                                <tr>
                                    <td colSpan="5" className="px-6 py-12 text-center text-gray-500">
                                        Kayıt bulunamadı
                                    </td>
                                </tr>
                            ) : (
                                filteredUsers.map((user) => (
                                    <tr key={user.id} className="hover:bg-gray-50/80 transition-colors group">
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center text-primary-600 font-bold">
                                                    {user.firstName[0]}{user.lastName[0]}
                                                </div>
                                                <div>
                                                    <p className="font-medium text-gray-900">{user.firstName} {user.lastName}</p>
                                                    <div className="flex items-center gap-1.5 text-xs text-gray-500 mt-0.5">
                                                        <Mail size={12} />
                                                        {user.email}
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            {user.role === 'super_admin' ? (
                                                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700 border border-purple-200">
                                                    <ShieldAlert size={12} />
                                                    Süper Admin
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700 border border-blue-200">
                                                    <Shield size={12} />
                                                    Admin
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700 border border-green-200">
                                                Active
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-500">
                                            <div className="flex items-center gap-1.5">
                                                <Calendar size={14} />
                                                {format(new Date(user.createdAt), 'd MMM yyyy', { locale: tr })}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                                <button
                                                    onClick={() => handleEdit(user)}
                                                    className="p-2 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                                                    title="Düzenle"
                                                >
                                                    <Edit2 size={16} />
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(user.id)}
                                                    className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                                    title="Sil"
                                                    disabled={user.role === 'super_admin'} // Basic protection, ideally handle in backend/logic more robustly
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            <AdminUserModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                user={selectedUser}
                onSave={handleSave}
                isLoading={isSaving}
            />
        </div>
    );
};

export default AdminUsers;
